import { ExpandLess, ExpandMore, FolderOpen } from '@mui/icons-material';
import {
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { useDrag, useDrop } from 'react-dnd';
import { WallFolder, WallFolderWithRelations } from 'types';
import { WallFolderWithWeatherRelations } from 'types/models/WallFolder';

import { useUpdateWall } from 'features/walls/api/updateWall';
import { useUpdateWeatherWall } from 'features/weatherWalls/api/updateWeatherWall';
import { queryClient } from 'lib/react-query';
import { useFolders } from './api/getFolders';
import { useWeatherFolders } from './api/getWeatherFolders';
import { useUpdateWeatherFolder } from './api/updateWeatherFolder';
import { useUpdateFolder } from './api/updateFolder';
import PublicIcon from '@mui/icons-material/Public';
import PersonIcon from '@mui/icons-material/Person';

interface Props {
  folder: WallFolderWithRelations | WallFolderWithWeatherRelations;
  selected: boolean;
  handleFolderClicked: (folder: WallFolder) => void;
  foldersOpenStatus: {
    [id: number]: boolean;
  };
}
const FolderListItem = ({
  folder,
  selected,
  handleFolderClicked,
  foldersOpenStatus,
}: Props) => {
  const user = useAppSelector((state) => state.auth.user);
  const { data: folders } = useFolders({ userId: user?.id });
  const { data: weatherFolders } = useWeatherFolders({ userId: user?.id });
  const updateFolderMutation = useUpdateFolder({});
  const updateWeatherFolderMutation = useUpdateWeatherFolder({});
  const updateWallMutation = useUpdateWall({});
  const updateWeatherWallMutation = useUpdateWeatherWall({});
  const [, drag] = useDrag(() => ({
    type: 'FOLDER',
    item: { name: folder.id, type: 'FOLDER' },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }));

  const checkIfFolderIsGlobal = (
    folder: WallFolderWithRelations | WallFolderWithWeatherRelations
  ): boolean => {
    if (!folder.hasOwnProperty('walls')) return false;
    const hasSynchedWall = (folder as WallFolderWithRelations).walls.filter(
      (wall) => wall.synched
    );
    return hasSynchedWall.length > 0;
  };

  const onDrop = async (item: { name: string; type: string }) => {
    if (item.type === 'WALL') {
      if ('walls' in folder) {
        await updateWallMutation.mutateAsync({
          data: {
            folder_id: folder.id,
            folder_position: 0,
          },
          wallId: Number(item.name),
        });
        for await (const wall of folder.walls) {
          if (wall.folder_position) {
            await updateWallMutation.mutateAsync({
              data: {
                folder_position: wall.folder_position + 1,
              },
              wallId: Number(wall.id),
            });
          }
        }
        queryClient.invalidateQueries(['folders']);
        queryClient.invalidateQueries(['walls']);
      } else if ('weatherWalls' in folder) {
        await updateWeatherWallMutation.mutateAsync({
          wallId: Number(item.name),
          data: {
            folder_id: folder.id,
            folder_position: 0,
          },
        });
        for await (const wall of folder.weatherWalls) {
          if (wall.folder_position) {
            await updateWeatherWallMutation.mutateAsync({
              wallId: Number(wall.id),
              data: {
                folder_position: wall.folder_position + 1,
              },
            });
          }
        }
        queryClient.invalidateQueries(['weatherFolders']);
        queryClient.invalidateQueries(['weatherWall']);
      }
    }
    if (item.type === 'FOLDER' && user && folder.position !== null) {
      if ('walls' in folder && folders) {
        await updateFolderMutation.mutateAsync({
          folderId: Number(item.name),
          data: { position: folder.position },
        });
        for await (const otherFolder of folders) {
          if (
            otherFolder.position !== null &&
            otherFolder.position !== undefined &&
            folder.position <= otherFolder.position &&
            otherFolder.id !== Number(item.name)
          ) {
            await updateFolderMutation.mutateAsync({
              data: { position: otherFolder.position + 1 },
              folderId: otherFolder.id,
            });
          }
        }
      } else if ('weatherWalls' in folder && weatherFolders) {
        await updateWeatherFolderMutation.mutateAsync({
          data: { position: folder.position },
          folderId: Number(item.name),
        });
        for await (const otherFolder of weatherFolders) {
          if (
            otherFolder.position !== null &&
            otherFolder.position !== undefined &&
            folder.position <= otherFolder.position &&
            otherFolder.id !== Number(item.name)
          ) {
            await updateWeatherFolderMutation.mutateAsync({
              data: { position: otherFolder.position + 1 },
              folderId: otherFolder.id,
            });
          }
        }
      }
    }
  };
  const [{ isOver }, drop] = useDrop({
    accept: ['WALL', 'FOLDER'],
    drop: onDrop,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });
  return (
    <div ref={drop}>
      <ListItem
        key={folder.id}
        button
        selected={selected || isOver}
        onClick={() => handleFolderClicked(folder)}
        ref={drag}
      >
        <ListItemText>
          <Grid container alignItems='center'>
            <FolderOpen sx={{ mr: '.5rem' }} color='primary' />
            {folder.name}
          </Grid>
        </ListItemText>
        <ListItemIcon>
          {checkIfFolderIsGlobal(folder) && (
            <Tooltip title='Global Folder'>
              <PublicIcon sx={{ mr: 1 }} />
            </Tooltip>
          )}
          {folder.user_id !== user?.id && (
            <Tooltip title='Folder owned by another user'>
              <PersonIcon />
            </Tooltip>
          )}
        </ListItemIcon>
        {foldersOpenStatus[folder.id] ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
    </div>
  );
};

export default FolderListItem;
